import React, { createContext, useContext, useState, useEffect } from "react";
import { getTheme } from "../lib/api";

const DEFAULT_THEME = {
    primary_color: "#62CB43",
    primary_dark: "#389622",
    secondary_color: "#F9D932",
    accent_color: "#E53E3E",
    text_color: "#333333",
    bg_color: "#FFFFFF",
    bg_light: "#F4F4F4",
    logo_text: "SOLARGREEN",
    logo_style: "classic",
    tagline: "Fotovoltaika & Chytrý Dům",
    phone: "+420 608 808 815",
    email: "info@solar-green.cz",
    show_top_bar: true,
    top_bar_text: "Doprava PPL od 199 Kč | Expedice do 48h",
    homepage_layout: "sidebar",
    hero_style: "hexagonal",
    hero_title: "FOTOVOLTAIKA",
    hero_subtitle: "PRO VAŠI DOMÁCNOST",
    hero_stat1: "500+ instalací v ČR",
    hero_stat2: "50+ milionů Kč ušetřeno pro zákazníky",
    sections_order: ["hero", "categories", "featured_products", "why_us", "brands", "blog", "cta"],
    sections_visible: { hero: true, categories: true, featured_products: true, why_us: true, brands: true, blog: true, cta: true },
    sidebar_categories: ["Loxone", "Solar Dock", "Fotovoltaika", "FVE Příslušenství", "FVE Konstrukce", "Wallboxy", "Služby", "Půjčovna"],
    nav_items: [
        { label: "VÝROBCI", href: "/kategorie", badge: "Top", badge_color: "#E53E3E", dropdown: true },
        { label: "FOTOVOLTAIKA", href: "/fve", badge: "", badge_color: "", dropdown: false },
        { label: "CARPORT", href: "/carporty", badge: "", badge_color: "", dropdown: false },
        { label: "DOTACE", href: "/sluzby", badge: "Žhavé", badge_color: "#F97316", dropdown: false },
        { label: "SERVIS", href: "/sluzby", badge: "", badge_color: "", dropdown: false },
        { label: "INFO", href: "/blog", badge: "Dokumenty", badge_color: "#6366f1", dropdown: false },
        { label: "BLOG", href: "/blog", badge: "", badge_color: "", dropdown: false },
        { label: "KONTAKT", href: "/sluzby", badge: "", badge_color: "", dropdown: false },
    ],
    border_radius: "4px",
    font_family: "Open Sans, Arial, sans-serif",
};

const ThemeContext = createContext({ theme: DEFAULT_THEME, setTheme: () => {} });

function applyThemeToCss(theme) {
    const root = document.documentElement;
    root.style.setProperty("--sg-primary", theme.primary_color || "#62CB43");
    root.style.setProperty("--sg-primary-dark", theme.primary_dark || "#389622");
    root.style.setProperty("--sg-secondary", theme.secondary_color || "#F9D932");
    root.style.setProperty("--sg-accent", theme.accent_color || "#E53E3E");
    root.style.setProperty("--sg-text", theme.text_color || "#333333");
    root.style.setProperty("--sg-bg", theme.bg_color || "#FFFFFF");
    root.style.setProperty("--sg-bg-light", theme.bg_light || "#F4F4F4");
    root.style.setProperty("--sg-radius", theme.border_radius || "4px");
    root.style.setProperty("--sg-font", theme.font_family || "Open Sans, Arial, sans-serif");
    // Layout width
    const widthMap = { full: "100%", wide: "1600px", standard: "1400px", narrow: "1200px" };
    root.style.setProperty("--sg-content-width", widthMap[theme.layout_width] || "100%");
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(DEFAULT_THEME);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        getTheme()
            .then(r => {
                const t = { ...DEFAULT_THEME, ...r.data };
                setThemeState(t);
                applyThemeToCss(t);
            })
            .catch(() => applyThemeToCss(DEFAULT_THEME))
            .finally(() => setLoaded(true));
    }, []);

    const setTheme = (t) => {
        const merged = { ...DEFAULT_THEME, ...t };
        setThemeState(merged);
        applyThemeToCss(merged);
    };

    // Apply immediately on every theme state change
    useEffect(() => {
        applyThemeToCss(theme);
    }, [theme]);

    if (!loaded) return null; // wait for theme before rendering

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}
